import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { returnMsg, simCardMsg, subChangePendingMsg, t } from '../../common/i18n/index.js';
import { SimType } from '../../generated/api/models.js';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { EmptyOrError } from '../EmptyOrError/index.js';
import { Loading } from '../Loading/index.js';
import { OrderEsim } from '../OrderEsim/OrderEsim.js';
import { OrderPhysicalSim } from '../OrderPhysicalSim/OrderPhysicalSim.js';
import type { SubscriptionCategory } from '../../common/enums.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { Address, ServiceFeeResponse, Subscription } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';

import './OrderSim.scss';

export interface OrderSimProps {
  breadCrumbs: JSX.Element;
  category: SubscriptionCategory;
  disallowBillableSimChanges: boolean;
  hasPendingActions: boolean;
  simTypeInitialSelection: SimType;
  subscription: Subscription;
  companyInfo?: CompanyInfoResponse;
  user?: AuthenticatedUser | null;
  getServiceFeeSim?: (
    simType: SimType,
    subscription: Subscription,
    address?: Address,
    user?: AuthenticatedUser | null
  ) => Promise<ServiceFeeResponse>;
  isEoe: boolean;
  onClickSubscription: () => void;
}

export const OrderSimComponent = (props: OrderSimProps) => {
  const { subscription, companyInfo, user, getServiceFeeSim, simTypeInitialSelection } = props;
  const [simChangeServiceFee, setSimChangeServiceFee] = useState<ServiceFeeResponse | undefined>(undefined);
  const [simType, setSimType] = useState<SimType>(simTypeInitialSelection);

  useEffect(() => {
    const fn = async () => {
      if (getServiceFeeSim) {
        setSimChangeServiceFee(undefined);
        const serviceFeeSim = await getServiceFeeSim(simType, subscription, companyInfo?.address, user);
        setSimChangeServiceFee(serviceFeeSim);
      }
    };
    void fn();
  }, [companyInfo, getServiceFeeSim, simType, subscription, user]);

  if (getServiceFeeSim && simChangeServiceFee === undefined) {
    return <Loading />;
  }

  const products = [
    {
      id: SimType.ESIM,
      name: 'eSIM',
      selected: simType === SimType.ESIM,
    },
    {
      id: SimType.PHYSICAL,
      name: t.PIZC(simCardMsg),
      selected: simType !== SimType.ESIM,
    },
  ];
  return (
    <>
      <h3 className={`ea-h3 ea-h3--thick ${dsClass.PADDING_BOTTOM_4}`}>{t.RPVC('Choose SIM card type')}</h3>
      <CL.SelectGroup
        className={dsClass.PADDING_0}
        onChange={selectedId => {
          if (selectedId) {
            setSimType(selectedId as SimType);
          }
        }}
        products={products}
        value={products.find(i => i.selected)?.id}
      />
      {simType === SimType.ESIM ? (
        <OrderEsim {...props} simChangeServiceFee={simChangeServiceFee?.price} onCancel={props.onClickSubscription} />
      ) : (
        <OrderPhysicalSim
          {...props}
          simChangeServiceFee={simChangeServiceFee?.price}
          onCancel={props.onClickSubscription}
        />
      )}
    </>
  );
};

export const OrderSim = (props: OrderSimProps) => {
  const { breadCrumbs, hasPendingActions, subscription, onClickSubscription } = props;

  if (!subscription) {
    return <Loading />;
  }

  if (hasPendingActions) {
    return (
      <EmptyOrError
        id="pending-sim-changes"
        text={t.MQHO(subChangePendingMsg)}
        onButtonClick={onClickSubscription}
        buttonText={t.VH59(returnMsg)}
      />
    );
  }

  const { subscriptionContactName, subscriptionPurposeOfUse, subscriptionUserFriendlyId } = subscription;

  return (
    <DetailsWrapper
      detailsTop={breadCrumbs}
      id="of-order-sim"
      heading={t.QMMA('Order new SIM')}
      headingBottom={(subscriptionContactName || subscriptionPurposeOfUse) + ' | ' + subscriptionUserFriendlyId}
      heroPicto="order-sim"
    >
      <OrderSimComponent {...props} />
    </DetailsWrapper>
  );
};
